import { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { toast } from 'react-toastify';
import { clearAuthStorage, storeAuthSession } from './helpers';
import { logger } from './logger';
import {
    clearSsoCallbackParams,
    exchangeSsoToken,
    hasSsoCallback,
    readSsoCallback,
} from './ssoHelper';

/**
 * Completes an SSO sign-in when the browser comes back from the SSO service.
 *
 * Mounted at the app root rather than on the login page: the SSO service returns to
 * whatever frontend URL is registered for this portal's appVar, which is not
 * necessarily "/login", so the token has to be picked up on any route.
 *
 * `processing` is seeded synchronously from the URL so the app can hold off rendering
 * a route — otherwise the avatar would briefly mount as a guest and then flip to a
 * signed-in session once the exchange resolves.
 */
export const useSsoCallback = (): { processing: boolean } => {
    const navigate = useNavigate();
    const [processing, setProcessing] = useState(() => hasSsoCallback(readSsoCallback()));

    useEffect(() => {
        const callback = readSsoCallback();
        if (!hasSsoCallback(callback)) return;

        // Drop the token from the address bar before anything else, so it never
        // survives in history or leaks through the Referer header.
        clearSsoCallbackParams();

        let cancelled = false;
        const fail = (message: string) => {
            if (cancelled) return;
            toast.error(message);
            setProcessing(false);
            navigate('/login', { replace: true });
        };

        if (callback.error) {
            fail(callback.error);
            return;
        }

        if (callback.twoFactor) {
            // The SSO service asked for a second factor. This app has no OTP screen, so
            // the only honest outcome is to send the user back to the password form.
            fail('This account requires a verification code. Please sign in with your email and password.');
            return;
        }

        exchangeSsoToken(callback.token as string)
            .then((content) => {
                if (cancelled) return;
                clearAuthStorage();
                storeAuthSession(content);
                toast.success('Logged in successfully!');
                setProcessing(false);
                navigate('/', { replace: true });
            })
            .catch(() => {
                // Never log the error object itself — it can carry the SSO token.
                logger.error('SSO sign-in failed.');
                fail('Single sign-on failed. Please try again or use your email and password.');
            });

        return () => {
            cancelled = true;
        };
    }, [navigate]);

    return { processing };
};
